/**
 * Composite grading / validation for multiple ordering exercises per content item.
 */

import { orderingGrader, type OrderingAnswer } from '@ai-content-hub/content-elements';
import type { GradingResult, ValidationResult } from '@ai-content-hub/content-kit';

import type { OrderingContentData } from './schema';
import { normalizeOrderingContentData } from './schema';

export type OrderingContentCompositeAnswer = Record<string, OrderingAnswer>;

export function normalizeOrderingContentAnswer(
    answer: unknown,
    exerciseIds: string[],
): OrderingContentCompositeAnswer {
    if (!answer || exerciseIds.length === 0) {
        return {};
    }
    if (Array.isArray(answer) && exerciseIds.length === 1) {
        return { [exerciseIds[0]]: answer as OrderingAnswer };
    }
    if (typeof answer === 'object' && !Array.isArray(answer)) {
        const o = answer as Record<string, unknown>;
        const out: OrderingContentCompositeAnswer = {};
        for (const id of exerciseIds) {
            const v = o[id];
            if (Array.isArray(v)) {
                out[id] = v as OrderingAnswer;
            }
        }
        return out;
    }
    return {};
}

export function gradeOrderingContentComposite(
    data: OrderingContentData,
    answer: unknown,
    config?: Record<string, unknown>,
): GradingResult {
    const normalized = normalizeOrderingContentData(data);
    const ids = normalized.exercises.map((e) => e.id);
    const byEx = normalizeOrderingContentAnswer(answer, ids);
    let score = 0;
    let max = 0;
    let allCorrect = true;
    const perExercise: GradingResult[] = [];
    for (const ex of normalized.exercises) {
        const r = orderingGrader.grade(ex.data, byEx[ex.id] ?? [], config);
        perExercise.push(r);
        score += r.score;
        max += r.max;
        if (!r.isCorrect) {
            allCorrect = false;
        }
    }
    return {
        isCorrect: allCorrect && normalized.exercises.length > 0,
        score,
        max,
        feedback: allCorrect ? 'Correct.' : 'Some ordering tasks need review.',
        details: { perExercise },
    };
}

export function validateOrderingContentComposite(data: OrderingContentData): ValidationResult {
    const normalized = normalizeOrderingContentData(data);
    const errors: ValidationResult['errors'] = [];
    normalized.exercises.forEach((ex, i) => {
        const v = orderingGrader.validate?.(ex.data);
        if (v && !v.isValid) {
            for (const e of v.errors) {
                errors.push({
                    field: `exercises.${i}.data.${e.field}`,
                    message: e.message,
                    code: e.code,
                });
            }
        }
    });
    return { isValid: errors.length === 0, errors };
}
